import { getGlobalLogger } from '@zg-rentals/logger-base';
import { getLsKey } from 'app/shared/utils/debugUtils';

const logger = getGlobalLogger('googleSignInUtils');

export interface GoogleSignInConfig {
  clientId: string;
  onCredentialResponse: (response: GoogleCredentialResponse) => void;
  onError?: (error: string) => void;
  onSuccess?: () => void;
  autoPrompt?: boolean;
  // Opt One Tap into the browser's FedCM API where supported (Chrome 117+, Edge).
  // Falls back to legacy One Tap on browsers without FedCM support.
  // https://developers.google.com/identity/gsi/web/guides/fedcm-migration
  useFedcmForPrompt?: boolean;
}

type GoogleCredentialResponse = {
  credential: string;
  select_by?: string;
  client_id?: string;
};

// FedCM removes the display-moment methods (the browser owns the UI surface).
// https://developers.google.com/identity/gsi/web/guides/fedcm-migration
type GooglePromptMomentNotification = {
  isSkippedMoment(): boolean;
  getSkippedReason(): string;
  isDismissedMoment(): boolean;
  getDismissedReason(): string;
  getMomentType(): string;
};

export interface GoogleSignInInstance {
  prompt: () => void;
  isInitialized: () => boolean;
  destroy: () => void;
}

class GoogleSignIn implements GoogleSignInInstance {
  private config: GoogleSignInConfig;
  private initialized = false;

  constructor(config: GoogleSignInConfig) {
    this.config = config;
  }

  initialize(): void {
    const debug = getLsKey('debugGoogle') === 'true';

    if (!window.google?.accounts?.id) {
      logger?.error('Google Sign In API not loaded');
      if (debug) console.error('[Google OneTap] window.google.accounts.id is not available');
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: this.config.clientId,
        callback: this.config.onCredentialResponse,
        ...(this.config.useFedcmForPrompt ? { use_fedcm_for_prompt: true } : {}),
      });

      this.initialized = true;
      if (debug) console.log('[Google OneTap] ✅ Initialization successful');

      if (this.config.autoPrompt !== false) {
        this.prompt();
      }

      if (this.config.onSuccess) {
        this.config.onSuccess();
      }
    } catch (error) {
      logger?.error('Failed to initialize Google Sign In:', error);
      if (debug) console.error('[Google OneTap] ❌ Initialization error:', error);
      if (this.config.onError) {
        this.config.onError('Failed to initialize Google Sign In');
      }
    }
  }

  prompt(): void {
    const debug = getLsKey('debugGoogle') === 'true';

    if (!this.initialized) {
      logger?.error('Google Sign In not initialized');
      return;
    }

    try {
      window.google.accounts!.id.prompt((notification: GooglePromptMomentNotification) => {
        if (notification.isSkippedMoment?.()) {
          logger?.info(`Google OneTap skipped: ${notification.getSkippedReason?.()}`);
        }
        if (notification.isDismissedMoment?.()) {
          // Persist dismissal so shouldShowOneTapPrompt skips re-prompting for 24h.
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('google_onetap_dismissed', Date.now().toString());
          }
        }
        if (debug) console.log('[Google OneTap] prompt notification', notification.getMomentType?.());
      });
    } catch (error) {
      logger?.error('Failed to show Google One Tap prompt:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  destroy(): void {
    if (window.google?.accounts?.id) {
      window.google.accounts!.id.cancel();
    }
    this.initialized = false;
  }
}

let instance: GoogleSignIn | null = null;

export const createGoogleSignIn = (config: GoogleSignInConfig): GoogleSignInInstance => {
  if (instance) {
    instance.destroy();
  }
  instance = new GoogleSignIn(config);
  instance.initialize();
  return instance;
};

export const decodeGoogleCredential = (
  credential: string,
): { email?: string; emailVerified?: boolean; name?: string } | null => {
  try {
    const parts = credential.split('.');
    if (parts.length !== 3) {
      logger?.warn('Invalid JWT format');
      return null;
    }
    const payload = JSON.parse(atob(parts[1]));
    return { email: payload.email, emailVerified: payload.email_verified, name: payload.name };
  } catch (error) {
    logger?.error('Failed to decode Google credential:', error);
    return null;
  }
};
